import { ApiClient } from '../apiClient';
import { apiClient } from '../apiClient'; // Para endpoints que devuelven respuesta directa

// ===============================
// INTERFACES KML
// ===============================

export interface UploadedKml {
  archivo_id: number;
  nombre_original: string;
  estado: 'pendiente' | 'procesando' | 'completado' | 'error';
  area_hectareas?: number | null; // ✅ Puede ser null para líneas
  cantidad_poligonos?: number;
  mensaje?: string;
  url_descarga?: string;
  // ✅ NUEVO: Soporte para líneas
  longitud_metros?: number | null;
  cantidad_lineas?: number;
  lineas?: any[];
  tipos_geometria?: string[];
  // Datos del frontend para manejo temporal
  file?: File;
  orden_en_faena?: number;
  // Datos para mapas (Checkpoint 4)
  poligonos?: any[];
  bounds?: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
}

export interface KmlValidationResult {
  es_valido: boolean;
  tipo_archivo: 'kml' | 'kmz';
  tamaño_mb: number;
  errores: string[];
  warnings: string[];
}

export interface KmlProcesamientoStatus {
  archivo_id: number;
  estado: string;
  progreso_porcentaje: number;
  mensaje: string;
  area_total_hectareas?: number;
  cantidad_poligonos?: number;
  tiene_superposiciones?: boolean;
  geometria_valida?: boolean;
}

export interface KmlPoligono {
  id: number | string;
  nombre: string;
  descripcion?: string;
  area_hectareas: number;
  area_metros2: number;
  perimetro_metros?: number;
  geometria: string; // WKT format
  centroide: string; // WKT format
  calidad_geometria?: number;
  es_valido: boolean;
  tiene_huecos: boolean;
  orden: number;
  // Para mapas temporales (Checkpoint 4)
  coordinates?: [number, number][];
}

// ===============================
// SERVICIO KML
// ===============================

export class KmlService {
  
  /**
   * Validación básica del frontend como fallback
   */
  static validacionBasicaFrontend(file: File): KmlValidationResult {
    const errores: string[] = [];
    const warnings: string[] = [];
    
    // Validar extensión
    if (!this.esArchivoKmlValido(file)) {
      errores.push('El archivo debe tener extensión .kml o .kmz');
    }
    
    // Validar tamaño
    const tamañoMB = file.size / (1024 * 1024);
    if (tamañoMB > 50) {
      errores.push(`Archivo demasiado grande (${tamañoMB.toFixed(1)}MB). Máximo: 50MB`);
    }
    
    // Validar nombre
    if (file.name.length > 255) {
      errores.push('Nombre de archivo demasiado largo');
    }
    
    if (tamañoMB > 10) {
      warnings.push('Archivo grande, el procesamiento puede tomar más tiempo');
    }
    
    const tipoArchivo = file.name.toLowerCase().endsWith('.kmz') ? 'kmz' : 'kml';
    
    return {
      es_valido: errores.length === 0,
      tipo_archivo: tipoArchivo,
      tamaño_mb: Number(tamañoMB.toFixed(2)),
      errores,
      warnings
    };
  }

  /**
   * Validar archivo KML antes de subirlo
   */
  static async validate(file: File): Promise<KmlValidationResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await ApiClient.post<KmlValidationResult>('/kml/validate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Verificar estructura de la respuesta
      if (response.success && response.data) {
        // Validar que tenga los campos requeridos
        if (typeof response.data.es_valido !== 'undefined') {
          return response.data;
        } else {
          console.warn('⚠️ Respuesta de API sin campo es_valido, usando validación frontend');
          return this.validacionBasicaFrontend(file);
        }
      } else {
        console.warn('⚠️ Respuesta de API sin éxito, usando validación frontend:', response);
        return this.validacionBasicaFrontend(file);
      }
    } catch (error) {
      console.error('Error validating KML, usando validación frontend:', error);
      
      // Si hay error de API, usar validación básica del frontend
      return this.validacionBasicaFrontend(file);
    }
  }

  /**
   * Calcular área Y longitud usando API /calcular-geometrias-temporales
   */
  static async calcularAreaReal(file: File): Promise<UploadedKml> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // 🔧 USAR ENDPOINT NUEVO que soporta polígonos Y líneas
      const response = await apiClient.post('/kml/calcular-geometrias-temporales', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!response.data) {
        throw new Error('Respuesta vacía del servidor');
      }
      
      // ✅ NUEVO: Validar que tenga datos de área O longitud
      const tienePoligonos = response.data.cantidad_poligonos > 0;
      const tieneLineas = response.data.cantidad_lineas > 0;
      
      if (!tienePoligonos && !tieneLineas) {
        console.error('❌ El archivo no contiene geometrías válidas:', response.data);
        throw new Error('El archivo KML no contiene polígonos ni líneas válidas');
      }

      return {
        archivo_id: Date.now(), // ID temporal para frontend
        nombre_original: file.name,
        estado: 'completado',
        area_hectareas: response.data.area_hectareas || null, // ✅ Puede ser null para líneas
        cantidad_poligonos: response.data.cantidad_poligonos,
        mensaje: response.data.mensaje,
        file: file,
        // ✅ NUEVO: Soporte para líneas
        longitud_metros: response.data.longitud_metros || null,
        cantidad_lineas: response.data.cantidad_lineas || 0,
        lineas: response.data.lineas || [],
        tipos_geometria: response.data.tipos_geometria || [],
        // Datos adicionales del cálculo real
        poligonos: response.data.poligonos || [],
        bounds: response.data.bounds,
        orden_en_faena: 1
      };

    } catch (error) {
      console.error('❌ Error completo en calcularAreaReal:', error);
      console.error('❌ Error.message:', error instanceof Error ? error.message : 'Error desconocido');
      throw new Error('Error al calcular geometrías del archivo KML');
    }
  }

  /**
   * Subir archivo KML temporal con cálculo real de área
   */
  static async uploadTemporal(file: File): Promise<UploadedKml> {
    try {
      // Usar API real para calcular área
      return await this.calcularAreaReal(file);
    } catch (error) {
      console.error('Error uploading KML:', error);
      throw new Error('Error al procesar archivo KML');
    }
  }

  /**
   * Subir archivo KML real (para checkpoint 5)
   */
  static async upload(file: File, reporteFaenaId: number): Promise<UploadedKml> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('reporte_faena_id', reporteFaenaId.toString());

      const response = await ApiClient.post<any>('/kml/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        archivo_id: response.data.archivo_id,
        nombre_original: response.data.nombre_original,
        estado: response.data.estado,
        mensaje: response.data.mensaje,
        url_descarga: response.data.url_descarga
      };

    } catch (error) {
      console.error('Error uploading KML:', error);
      throw new Error('Error al subir archivo KML');
    }
  }

  /**
   * Obtener estado del procesamiento
   */
  static async getStatus(archivoId: number): Promise<KmlProcesamientoStatus> {
    try {
      const response = await ApiClient.get<any>(`/kml/${archivoId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting KML status:', error);
      throw new Error('Error al obtener estado del procesamiento');
    }
  }

  /**
   * Obtener polígonos procesados de un archivo KML
   */
  static async getPoligonos(archivoId: number): Promise<KmlPoligono[]> {
    try {
      const response = await ApiClient.get(`/kml/${archivoId}/poligonos`);
      
      // Validar que la respuesta sea un array
      if (!Array.isArray(response)) {
        console.warn('Respuesta de polígonos no es array:', response);
        return [];
      }
      
      return response;
    } catch (error) {
      console.error('Error getting KML polygons:', error);
      // Devolver array vacío en lugar de throw para que no falle el modal
      return [];
    }
  }

  /**
   * ✅ NUEVO: Obtener todas las geometrías (polígonos + líneas) de un archivo KML
   */
  static async getGeometrias(archivoId: number): Promise<KmlPoligono[]> {
    try {
      const response = await ApiClient.get<any>(`/kml/${archivoId}/geometrias`);
      
      // El endpoint devuelve { poligonos: [], lineas: [] }
      if (!response || typeof response !== 'object') {
        console.warn('Respuesta de geometrías no es objeto:', response);
        return [];
      }
      
      const geometrias: KmlPoligono[] = [];
      
      // Agregar polígonos
      if (Array.isArray(response.poligonos)) {
        response.poligonos.forEach((poligono: any) => {
          geometrias.push({
            ...poligono,
            area_hectareas: poligono.area_hectareas || 0,
            area_metros2: poligono.area_metros2 || 0,
            orden: poligono.orden || 0,
            es_valido: poligono.es_valido || true,
            tiene_huecos: poligono.tiene_huecos || false
          });
        });
      }
      
      // Agregar líneas
      if (Array.isArray(response.lineas)) {
        response.lineas.forEach((linea: any) => {
          geometrias.push({
            id: linea.id,
            nombre: linea.nombre,
            descripcion: linea.descripcion,
            area_hectareas: 0, // Líneas no tienen área
            area_metros2: 0,
            perimetro_metros: linea.longitud_metros, // Usar longitud como perímetro
            geometria: linea.geometria,
            centroide: linea.centroide,
            calidad_geometria: linea.calidad_geometria,
            es_valido: linea.es_valido || true,
            tiene_huecos: false,
            orden: linea.orden || 0,
            longitud_metros: linea.longitud_metros,
            tipo_geometria: 'LINESTRING',
          } as any);
        });
      }
      
      return geometrias;
    } catch (error) {
      console.error('Error getting KML geometries:', error);
      // Devolver array vacío en lugar de throw para que no falle el modal
      return [];
    }
  }

  /**
   * Eliminar archivo KML
   */
  static async delete(archivoId: number): Promise<void> {
    try {
      await ApiClient.delete(`/kml/${archivoId}`);
    } catch (error) {
      console.error('Error deleting KML:', error);
      throw new Error('Error al eliminar archivo KML');
    }
  }

  /**
   * Obtener KMLs por faena
   */
  static async getByFaena(faenaId: number): Promise<UploadedKml[]> {
    try {
      const response = await ApiClient.get<any>(`/kml/faena/${faenaId}`);
      
      // Convertir respuesta a formato UploadedKml
      return response.data.archivos.map((archivo: any) => ({
        archivo_id: archivo.id,
        nombre_original: archivo.nombre_archivo_original,
        estado: archivo.estado_procesamiento,
        area_hectareas: archivo.area_total_hectareas,
        cantidad_poligonos: archivo.cantidad_poligonos,
        orden_en_faena: archivo.orden_en_faena
      }));

    } catch (error) {
      console.error('Error getting KMLs by faena:', error);
      throw new Error('Error al cargar archivos KML');
    }
  }

  // ELIMINADO: simularProcesamiento - ya no se necesita
  // El cálculo de área se hace directamente en calcularAreaReal()

  /**
   * Esperar procesamiento con polling
   */
  static async waitForProcessing(archivoId: number, maxIntentos: number = 10): Promise<KmlProcesamientoStatus> {
    let intentos = 0;
    
    while (intentos < maxIntentos) {
      const status = await this.getStatus(archivoId);
      
      if (status.estado === 'completado') {
        return status;
      }
      
      if (status.estado === 'error') {
        throw new Error('Error procesando archivo KML');
      }
      
      // Esperar 1 segundo antes del próximo intento
      await new Promise(resolve => setTimeout(resolve, 1000));
      intentos++;
    }
    
    throw new Error('Timeout esperando procesamiento del archivo KML');
  }

  /**
   * Calcular área total de múltiples KMLs
   */
  static calcularAreaTotal(kmls: UploadedKml[]): number {
    return kmls.reduce((total, kml) => {
      return total + (kml.area_hectareas || 0);
    }, 0);
  }

  /**
   * ✅ NUEVO: Calcular longitud total de múltiples KMLs
   */
  static calcularLongitudTotal(kmls: UploadedKml[]): number {
    return kmls.reduce((total, kml) => {
      return total + (kml.longitud_metros || 0);
    }, 0);
  }

  /**
   * ✅ NUEVO: Calcular bounds a partir de polígonos
   */
  static calcularBounds(poligonos: KmlPoligono[]): { min_lat: number; max_lat: number; min_lon: number; max_lon: number; } | undefined {
    if (!poligonos || poligonos.length === 0) {
      return undefined;
    }
    
    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;
    
    poligonos.forEach(poligono => {
      if (poligono.coordinates && poligono.coordinates.length > 0) {
        poligono.coordinates.forEach(([lon, lat]) => {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
        });
      }
    });
    
    if (minLat === Infinity) {
      return undefined;
    }
    
    return {
      min_lat: minLat,
      max_lat: maxLat,
      min_lon: minLon,
      max_lon: maxLon
    };
  }

  /**
   * ✅ NUEVO: Convertir geometría WKT a coordenadas para el mapa
   */
  static convertirWKTACoordenadas(wktGeometria: string): [number, number][] {
    try {
      // Manejar POLYGON
      const polygonMatch = wktGeometria.match(/POLYGON\(\(([^)]+)\)\)/);
      if (polygonMatch) {
        const coordenadasString = polygonMatch[1];
        const puntosWKT = coordenadasString.split(',');
        
        return puntosWKT.map(punto => {
          const [lng, lat] = punto.trim().split(' ').map(Number);
          return [lng, lat] as [number, number];
        });
      }
      
      // Manejar LINESTRING
      const lineMatch = wktGeometria.match(/LINESTRING\(([^)]+)\)/);
      if (lineMatch) {
        const coordenadasString = lineMatch[1];
        const puntosWKT = coordenadasString.split(',');
        
        return puntosWKT.map(punto => {
          const [lng, lat] = punto.trim().split(' ').map(Number);
          return [lng, lat] as [number, number];
        });
      }
      
      throw new Error('Formato WKT no soportado');
      
    } catch (error) {
      console.error('Error convirtiendo WKT a coordenadas:', error);
      return [];
    }
  }

  /**
   * Validar tipos de archivo permitidos
   */
  static esArchivoKmlValido(file: File): boolean {
    const extensionesPermitidas = ['.kml', '.kmz'];
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return extensionesPermitidas.includes(extension);
  }

  /**
   * Formatear tamaño de archivo
   */
  static formatearTamaño(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  }
}

export default KmlService;